import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { useFetcher } from "@remix-run/react";
import { FormEvent, useEffect, useRef, useState } from "react";
import {
  IModuleWebFormFields,
  SpecificLocaleFields,
} from "~/@types/generated/contentful";
import { RECAPTCHA_ACTION, RECAPTCHA_SITE_KEY } from "~/config";
import { getComponentFromContentfulRichTextEntry } from "~/contentful";
import { RoundedCta } from "../ctas";
import { Arrow } from "../icons";
import EmailField from "./formFields/EmailField";
import MultiLineTextField, {
  MULTILINE_MAX_LENGTH,
} from "./formFields/MultiLineTextField";
import SingleLineTextField from "./formFields/SingleLineTextField";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
    };
  }
}

type FormBuilderModuleProps = {
  fields: SpecificLocaleFields<IModuleWebFormFields>;
};

function validateField(
  entry: SpecificLocaleFields<IModuleWebFormFields>["fields"][number],
  value: string,
) {
  const { required, label } = entry.fields;
  const emailPattern = /^\w[\w.-]*@([\w-]+\.)+[\w-]+$/;
  const trimmed = value.trim();
  const contentType = entry.sys.contentType.sys.id;
  const requiredMessage = `${label} is required.`;

  switch (contentType) {
    case "itemEmailSubmission": {
      if (!trimmed) return required ? requiredMessage : undefined;

      return emailPattern.test(trimmed)
        ? undefined
        : "Enter a valid email address.";
    }
    case "itemMultiLineText": {
      if (required && !trimmed) return requiredMessage;
      if (value.length > MULTILINE_MAX_LENGTH) {
        return `${label} is over the ${MULTILINE_MAX_LENGTH}-character limit.`;
      }
      return undefined;
    }
    default:
      return required && !trimmed ? requiredMessage : undefined;
  }
}

function getSalesforceFieldName(
  entry: SpecificLocaleFields<IModuleWebFormFields>["fields"][number],
) {
  if (entry.fields.salesforceFieldName) return entry.fields.salesforceFieldName;

  switch (entry.sys.contentType.sys.id) {
    case "itemEmailSubmission":
      return "Email";
    case "itemMultiLineText":
      return "Enquiry";
    default:
      return entry.fields.label;
  }
}

async function getRecaptchaToken(): Promise<string> {
  if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return "";

  return new Promise((resolve) => {
    window.grecaptcha?.ready(async () => {
      window.grecaptcha
        ?.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION })
        .then(resolve)
        .catch(() => resolve(""));
    });
  });
}

export default function FormBuilderModule({ fields }: FormBuilderModuleProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [honeypot, setHoneypot] = useState("");
  const renderedAtRef = useRef(Date.now());
  const fetcher = useFetcher<{ success: boolean; error?: string }>();

  useEffect(() => {
    if (
      !RECAPTCHA_SITE_KEY ||
      document.querySelector('script[src*="recaptcha/api.js"]')
    )
      return;

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;

    document.head.appendChild(script);
  }, []);

  const handleChange = (id: string, value: string) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => (prev[id] ? { ...prev, [id]: undefined } : prev));
  };

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.success === true) {
      setValues({});
      setErrors({});
      setHoneypot("");
    }
  }, [fetcher.state, fetcher.data]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string | undefined> = {};

    fields.fields.forEach((entry) => {
      nextErrors[entry.sys.id] = validateField(
        entry,
        values[entry.sys.id] ?? "",
      );
    });

    setErrors(nextErrors);
    if (!Object.values(nextErrors).every((error) => !error)) return;

    const recaptchaToken = await getRecaptchaToken();
    fetcher.submit(
      {
        honeypot,
        renderedAt: renderedAtRef.current,
        recaptchaToken,
        subject: fields.subject,
        recipientEmails: fields.recipientEmails,
        fields: fields.fields.map((entry) => ({
          label: entry.fields.label,
          salesforceFieldName: getSalesforceFieldName(entry),
          value: values[entry.sys.id] ?? "",
        })),
      },
      {
        method: "post",
        action: "/api/submit-form",
        encType: "application/json",
      },
    );
  };

  let usedEmailField = false;
  const showSuccess =
    fetcher.state === "idle" && fetcher.data?.success === true;
  const showError = fetcher.state === "idle" && fetcher.data?.success === false;

  return (
    <section
      data-content-type="moduleWebForm"
      data-module-title={fields.title}
      className="form-builder col-span-full"
    >
      <div className="global-container">
        <div className="global-grid">
          <div className="wrapper">
            <h2 className="heading">{fields.title}</h2>
            <form noValidate onSubmit={handleSubmit} className="form">
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                className="honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              {showSuccess ? (
                <div className="success" role="status">
                  {fields.successMessage
                    ? documentToReactComponents(
                        fields.successMessage,
                        getComponentFromContentfulRichTextEntry,
                      )
                    : ""}
                </div>
              ) : (
                <>
                  {fields.fields.map((entry) => {
                    const id = entry.sys.id;
                    const contentType = entry.sys.contentType.sys.id;
                    const commonProps = {
                      id,
                      label: entry.fields.label,
                      required: entry.fields.required,
                      value: values[id] ?? "",
                      error: errors[id],
                      onChange: (value: string) => handleChange(id, value),
                    };

                    switch (contentType) {
                      case "itemEmailSubmission":
                        if (usedEmailField) return null;
                        usedEmailField = true;
                        return <EmailField key={id} {...commonProps} />;
                      case "itemMultiLineText":
                        return <MultiLineTextField key={id} {...commonProps} />;
                      default:
                        return (
                          <SingleLineTextField key={id} {...commonProps} />
                        );
                    }
                  })}
                  {fields.optionalText && (
                    <div className="optional-text">
                      {documentToReactComponents(
                        fields.optionalText,
                        getComponentFromContentfulRichTextEntry,
                      )}
                    </div>
                  )}
                  <RoundedCta
                    as="button"
                    kind="black"
                    label="Submit"
                    icon={<Arrow />}
                    className="submit"
                    disabled={fetcher.state !== "idle"}
                  />
                  {showError && (
                    <p className="submit-error" role="alert">
                      {fetcher.data?.error}
                    </p>
                  )}
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
